import { useState } from 'react';
import type { Transaction } from '../../../hooks/useTransactions';
import {
  PAYMENT_METHOD_MODAL_OPTIONS,
  PAYMENT_METHOD_LABELS,
} from '../../../lib/paymentMethods';

// Re-export for backwards compatibility with imports from this file
export { PAYMENT_METHOD_LABELS };
export type PaymentMethodDetailValue = typeof PAYMENT_METHOD_MODAL_OPTIONS[number]['value'];

/** @deprecated Use PAYMENT_METHOD_MODAL_OPTIONS from lib/paymentMethods instead */
export const PAYMENT_METHODS = PAYMENT_METHOD_MODAL_OPTIONS;

interface PaymentDetailModalProps {
  open: boolean;
  transaction: Transaction | null;
  accountName?: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (txId: string, detail: string) => Promise<void>;
}

export function PaymentDetailModal({
  open,
  transaction,
  accountName,
  loading = false,
  onClose,
  onConfirm,
}: PaymentDetailModalProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (!selected) { setError('Selecione a forma de pagamento.'); return; }
    if (!transaction) return;
    setError('');
    await onConfirm(transaction.id, selected);
    setSelected(null);
  };

  const handleClose = () => {
    setSelected(null);
    setError('');
    onClose();
  };

  if (!open || !transaction) return null;

  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl max-w-sm w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-circle-line text-emerald-500 text-lg" />
            </div>
            <div>
              <h3 className="text-base font-700 text-gray-900">Como foi pago?</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {transaction.description} · <span className="font-600 text-gray-600">{fmt(transaction.amount)}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Account info */}
        {accountName && (
          <div className="mx-6 mt-4 flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-xl border border-gray-100">
            <i className="ri-bank-line text-gray-400 text-sm" />
            <p className="text-xs text-gray-600 font-500">
              Saindo da conta <span className="font-700 text-gray-800">{accountName}</span>
            </p>
          </div>
        )}

        {/* Payment method grid */}
        <div className="px-6 pt-4 pb-2">
          <p className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-3">Forma de pagamento</p>
          <div className="grid grid-cols-4 gap-2">
            {PAYMENT_METHOD_MODAL_OPTIONS.map(pm => (
              <button
                key={pm.value}
                type="button"
                onClick={() => { setSelected(pm.value); setError(''); }}
                className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border text-[11px] font-600 transition-all cursor-pointer ${
                  selected === pm.value
                    ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <i className={`${pm.icon} text-lg ${selected === pm.value ? 'text-emerald-500' : pm.color}`} />
                {pm.label}
              </button>
            ))}
          </div>
          {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm cursor-pointer hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading || !selected}
            className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-sm cursor-pointer disabled:opacity-50 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
          >
            {loading && <i className="ri-loader-4-line animate-spin text-sm" />}
            {loading ? 'Marcando...' : 'Confirmar pagamento'}
          </button>
        </div>
      </div>
    </div>
  );
}
